/**
 * Declared user type -> default terminology register (CIR-2026-544).
 *
 * Guided Interface programme (Epics 26-28). A joining client user declares
 * their type — business owner or security professional — as a mandatory
 * step of the Epic 2 invite-accept flow (Epic 26 FR-09). This module derives
 * the DEFAULT terminology register from that declaration: business owner ->
 * plain-English, security professional -> technical (Epic 26 FR-10).
 *
 * The register is always DERIVED live from the declared type — never stored
 * separately — so changing the declared type takes immediate effect
 * everywhere it is read (Epic 26 FR-11): there is nothing to keep in sync.
 *
 * Consuming the register to render plain-English vs technical CONTENT is
 * Epic 26 FR-12 / Epics 27-28 — out of scope for this CIR. This module only
 * exposes the attribute + derivation so those consumers have it to read.
 */

export type DeclaredUserType = "business_owner" | "security_professional";

export type TerminologyRegister = "plain_english" | "technical";

/** The exact two permitted values, matching the DB CHECK constraint. */
export const DECLARED_USER_TYPES: readonly DeclaredUserType[] = [
  "business_owner",
  "security_professional",
];

export const DECLARED_USER_TYPE_LABELS: Record<DeclaredUserType, string> = {
  business_owner: "Business owner",
  security_professional: "Security professional",
};

export const DECLARED_USER_TYPE_DESCRIPTIONS: Record<DeclaredUserType, string> = {
  business_owner:
    "I run or manage the business. Show me plain-English guidance.",
  security_professional:
    "I work in IT, security or compliance. Show me technical detail.",
};

/**
 * Type guard for a value read back from the database or a form control.
 */
export function isDeclaredUserType(value: unknown): value is DeclaredUserType {
  return value === "business_owner" || value === "security_professional";
}

/**
 * Derives the default terminology register from a declared user type.
 * Falls back to the safer "plain_english" default for accounts that have not
 * (yet) declared a type — e.g. accounts that pre-date this feature.
 */
export function deriveDefaultRegister(
  declaredType: DeclaredUserType | null | undefined,
): TerminologyRegister {
  return declaredType === "security_professional" ? "technical" : "plain_english";
}

/** Type guard for a value read back from client_users.active_register. */
export function isTerminologyRegister(value: unknown): value is TerminologyRegister {
  return value === "plain_english" || value === "technical";
}

/**
 * Resolves the ACTIVE terminology register a signed-in client user currently
 * sees (CIR-2026-546, Epic 26 FR-11/FR-12, BR-05, BR-07): an explicit
 * per-user override (client_users.active_register) always wins; otherwise
 * this falls back to the register derived from the declared type.
 *
 * Active register = COALESCE(client_users.active_register, defaultRegister).
 *
 * PRESENTATION-ONLY: the result selects only which content variant renders.
 * It must never be read by any RLS/authz expression, the progress path
 * (guided_checklist_progress), the sequencing path (guided_checklist_steps),
 * or any compliance table — enforced live by
 * guided_rls_policies_referencing_presentation_columns().
 */
export function resolveActiveRegister(
  activeRegisterOverride: TerminologyRegister | null | undefined,
  declaredType: DeclaredUserType | null | undefined,
): TerminologyRegister {
  return isTerminologyRegister(activeRegisterOverride)
    ? activeRegisterOverride
    : deriveDefaultRegister(declaredType);
}
